/**
 * PRODUCT PAGE SCRIPT (2026-10-20) - what a configurator product page does in the browser, in one cached file instead
 * of a copy inline in every section. On it: the FRAME (snippets/buy-box-frame on the product section, main-product) and,
 * since 2026-10-22, the CUBE (snippets/buy-box-cube, on the product section too); since 2026-10-23 the preset CUBE Set
 * and CUBE Collection (snippets/buy-box-set, on the product section since 2026-10-24), several cities sold as one
 * variant; since 2026-10-24 BLOK (snippets/buy-box-blok), one line and no add-ons. Each
 * moved over checked with tools/pdp/contract.mjs (the cart lines, the requests, tracking and totals must stay the same).
 *
 * The section writes only what Liquid knows, before this file runs:
 *   window.CF.CONSTANTS   SELECTED_VARIANT_ID, MAX_SLOTS, PLAQUE_UNIT_CENTS, PLAQUE_MATERIALS {steel, brass: {id, priceCents}},
 *                         BREAKPOINT (phones below it, default 700); PLAQUE_GALLERY {desktop, mobile: {height, top}} where
 *                         the plaque is drawn on the gallery's main picture (the CUBE)
 *   window.CF.LOCALE      currency, moneyFormat, countryCode
 *   window.CF.T           atc {add_to_cart, adding, error_generic}, validation {select_city, city_out_of_stock,
 *                         enter_plaque_text}, plaque_error {emoji_not_allowed}
 *   window.CF.CART        attribute (the cart attribute naming the page, e.g. frame_configurator),
 *                         shipping (true: the line carries the ship window, CF_SHIPPING_LABEL, from the store's
 *                         shipping calendar - assets/cf-ship.js - or none where it is made from stock),
 *                         stock (true: an empty shelf blocks the button unless the variant is made to order, `backorder`);
 *                         keep (the localStorage key each city's cover and plaque are kept under, the CUBE's
 *                         cf-cube-setup: "THE SETUP IS KEPT");
 *                         on a Set or Collection also variantInput (the input holding the variant that goes in the cart,
 *                         #TrioVariant / #CollectionVariant - #City1..N then hold its cities), perCity (true: every add-on
 *                         comes once per city, CONSTANTS.MAX_SLOTS of them: one cover line of that quantity, a plaque line
 *                         per city with text), cities (the city titles in slot order, each plaque line's City) and
 *                         singlesCents (the cities bought one by one, snippets/set-singles: what the price is struck
 *                         against and the saving line counts from, instead of a compare-at)
 *   window.CF.PRODUCT_DATA where the page sells a display cover (the CUBE): cover {acrylicId, acrylicPriceCents,
 *                         glassId, glassPriceCents}, overlay / overlayFull {acrylic, glass} (the cover drawn on the picture)
 *   window.CF_VARIANT_DATA { <kind>: [variant records] } - the variant the page renders
 *   window.CF_SHIPPING_LABEL the localized name of the shipping property (shared with GRAND and BLOK)
 * and the markup: #City1 (the variant; on a Set or Collection its first city), the plaque add-on (#addon-plaque-id, #option-button-plaque-steel/-brass,
 * #plaque-design-btn, snippets/modal_plaque_configurator), the cover add-on (#display-cover-acrylic/-glass inside
 * #option-button-acrylic/-glass; on the CUBE page its window, assets/cf-cover-window.js), the gallery (#configurator-carousel, #configurator-dots; a Set's row of city cards,
 * .cf-trio-cell, each wearing its own city's cover and plaque), the totals (price-list js: true) and the Add to cart
 * buttons (.add-all-to-cart). The rotation slide has its own file (assets/cf-rotation.js).
 *
 * Shared with other scripts: window.CF_VARIANT_LOOKUP (magnifier-modal.js reads it), window.CFPlaque and
 * window.CFCover; read from the plaque designer: window.CFPlaqueWords. Everything else stays inside this file.
 */
(() => {
  // Loaded once per page: the product section and the FRAME section each load it after their buy box.
  if (window.CF_PDP_LOADED) return;
  window.CF_PDP_LOADED = true;
  const CF = (window.CF = window.CF || {});
  CF.STATE = CF.STATE || {
    choices: { cover: 'none', plaque: false },
    plaqueMaterial: 'steel',
    scroll: { locked: false, savedY: 0 }
  };
  const $ = (id) => document.getElementById(id);
  const isMobile = () => matchMedia(`(max-width:${(CF.CONSTANTS?.BREAKPOINT || 700) - 1}px)`).matches;
  // Pages that draw the add-ons on the gallery's main picture: the plaque (PLAQUE_GALLERY) and the cover (overlay).
  const drawsPlaque = () => !!CF.CONSTANTS?.PLAQUE_GALLERY;
  const drawsCover = () => !!CF.PRODUCT_DATA?.overlay;
  const sellsCover = () => !!CF.PRODUCT_DATA?.cover;
  // The input holding what goes in the cart: #City1, or a Set's / Collection's own variant (CF.CART.variantInput).
  const soldInput = () => $(CF.CART?.variantInput || 'City1');
  // How many of each add-on one pick buys: one, or one per city on a Set or Collection.
  const addonQty = () => (CF.CART?.perCity ? Number(CF.CONSTANTS?.MAX_SLOTS) || 1 : 1);
  // Whether a plaque is chosen: its input is enabled. A page that sells no plaque (BLOK) has no input, which is no plaque
  // - read as `!input?.disabled` it counted as one, in the total and on the pictures.
  const plaqueOn = () => {
    const input = $('addon-plaque-id');
    return !!input && !input.disabled;
  };

  // ------------------------------------------------------------------------------------------------ the variant
  // One record per kind (the variant this page renders): every reader looks up byId[City1.value].
  (() => {
    const normTitle = (t) => String(t || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const byId = {};
    Object.entries(window.CF_VARIANT_DATA || {}).forEach(([kind, list]) => {
      (list || []).forEach((v) => { byId[String(v.id)] = { ...v, size: kind }; });
    });
    window.CF_VARIANT_LOOKUP = { byId, normTitle };
  })();

  function cfGetVariantById(id) {
    return window.CF_VARIANT_LOOKUP?.byId?.[String(id)] || null;
  }

  // A variant flagged `backorder` is one we produce to order (snippets/cube-backorder-allowed), so an empty shelf does
  // not stop the sale. The only stock test on the page, so the button and the submit-time check can never disagree.
  function cfIsOutOfStock(variant) {
    if (!variant) return true;
    if (variant.backorder === true) return false;
    return Number(variant.inventoryQty) < 1;
  }

  function cfGetSelectedVariant(slotIdx) {
    const sel = $(`City${slotIdx}`);
    if (!sel || !sel.value) return null;
    return cfGetVariantById(sel.value);
  }

  function cfSafeOn(id, event, handler, options) {
    const el = $(id);
    if (!el) return;
    el.addEventListener(event, handler, options || false);
    return el;
  }

  // ------------------------------------------------------------------------------------------------ money
  // As checkout writes it, in the page's language and market (window.cfMoney, snippets/js-variables).
  function formatCents(cents) {
    return window.cfMoney(cents, CF.LOCALE.moneyFormat);
  }

  // The price this page sells - total, compare-at, saving - without ".00" when it has no cents, as price-list prints it.
  function formatPrice(cents) {
    return window.cfPrice(cents, CF.LOCALE.moneyFormat);
  }

  function cfMoneyStringFromCents(cents) {
    return (Number(cents || 0) / 100).toFixed(2);
  }

  // What an add-on pick adds to the order, "+€120.00": on a Set of three, three covers (price-list, role addon).
  function addonPrice(unitCents) {
    return '+' + formatCents(unitCents * addonQty());
  }

  // ------------------------------------------------------------------------------------------------ tracking
  // A cover or plaque pick, as CustomizeProduct for our own tracking (assets/cf-tracking.js).
  function trackCustomizePick(product, value) {
    if (!window.cfTrack) return;
    const variantId = String(product.variant_id || product.id);
    window.cfTrack('CustomizeProduct', {
      currency: CF.LOCALE.currency,
      value: Number(value),
      content_ids: [variantId],
      contents: [{ id: variantId, quantity: 1, item_price: Number(value) }],
      content_name: product.name,
      content_category: product.category
    });
  }

  function pushDisplayCoverDL(coverType) {
    if (coverType === 'none') return;
    const cover = CF.PRODUCT_DATA.cover;
    const variantId = coverType === 'acrylic' ? cover.acrylicId : cover.glassId;
    const priceCents = coverType === 'acrylic' ? cover.acrylicPriceCents : cover.glassPriceCents;
    trackCustomizePick({ id: String(variantId), name: 'Display Cover', category: 'Display Cover' }, cfMoneyStringFromCents(priceCents));
  }

  function pushPlaqueDL(material) {
    if (material === 'none') return;
    const def = CF.CONSTANTS.PLAQUE_MATERIALS?.[material];
    if (!def) return;
    trackCustomizePick({ id: String(def.id), name: 'Engraved Plaque', category: 'Engraved Plaque' }, cfMoneyStringFromCents(def.priceCents));
  }

  // ------------------------------------------------------------------------------------------------ messages
  function showError(message) {
    document.querySelectorAll('.error-note').forEach((el) => {
      el.textContent = message;
      el.style.display = 'block';
    });
  }

  function clearError() {
    document.querySelectorAll('.error-note').forEach((el) => {
      el.textContent = '';
      el.style.display = 'none';
    });
  }

  // ------------------------------------------------------------------------------------------------ totals
  function updateDisplayCoverTotal() {
    const cover = CF.PRODUCT_DATA.cover;
    const acrylicTotalEl = $('price-acrylic-total');
    const glassTotalEl = $('price-glass-total');
    if (acrylicTotalEl) acrylicTotalEl.innerHTML = addonPrice(cover.acrylicPriceCents);
    if (glassTotalEl) glassTotalEl.innerHTML = addonPrice(cover.glassPriceCents);
  }

  function updatePlaqueTotal() {
    const materials = CF.CONSTANTS.PLAQUE_MATERIALS || {};
    ['steel', 'brass'].forEach((mat) => {
      const def = materials[mat];
      if (!def) return;
      const totalEl = $(`price-plaque-${mat}-total`);
      if (totalEl) totalEl.textContent = addonPrice(Number(def.priceCents) || 0);
    });
  }

  function updateTotalSummary() {
    const priceEls = document.querySelectorAll('.js-total-price');
    const compareEls = document.querySelectorAll('.js-total-compare');
    const sel = soldInput();

    if (!sel || !sel.value) {
      priceEls.forEach((el) => { el.textContent = formatPrice(0); });
      return;
    }

    const v = cfGetVariantById(sel.value);
    let totalFinal = Number(v?.priceCents) || 0;
    // A Set or Collection is struck against its cities bought one by one today (snippets/set-singles), never a
    // compare-at typed on its variant; its saving is the set's alone - a clearance city is at its own price both ways.
    const singles = CF.CART?.singlesCents;
    let totalCompare = singles != null ? Number(singles) || 0 : Number(v?.compareCents) || totalFinal;
    const saving = Math.max(totalCompare - totalFinal, 0);

    // The add-ons, the plaque at the selected material's price, on BOTH totals: an add-on costs the same on a
    // discounted city, so the saving stays on screen (Alex, 2026-10-15). Once per city on a Set or Collection.
    let addOns = 0;
    if (sellsCover()) {
      const cover = getSelectedDisplayCover();
      if (cover === 'acrylic') addOns += CF.PRODUCT_DATA.cover.acrylicPriceCents;
      if (cover === 'glass') addOns += CF.PRODUCT_DATA.cover.glassPriceCents;
    }
    if (plaqueOn()) {
      const mat = CF.CONSTANTS.PLAQUE_MATERIALS?.[CF.STATE?.plaqueMaterial];
      addOns += Number(mat?.priceCents) || Number(CF.CONSTANTS.PLAQUE_UNIT_CENTS) || 0;
    }
    addOns *= addonQty();
    totalFinal += addOns;
    totalCompare += addOns;

    priceEls.forEach((el) => { el.innerText = formatPrice(totalFinal); });
    window.cfKlarnaAmount?.(totalFinal); // Klarna's line quotes what the page sells, the add-ons included (klarna-message)
    // "You save €40 as a Set": the amount in its own span, the line around it shown or hidden (price-list).
    if (singles != null) {
      document.querySelectorAll('.js-total-saving').forEach((el) => {
        const line = el.closest('.price-list__saving') || el;
        el.innerText = formatPrice(saving);
        line.hidden = saving <= 0;
      });
    }
    // The amount goes into its own span and the <compare-at-price> around it is shown or hidden, which keeps the
    // "Regular price" label for screen readers - see price-list.
    compareEls.forEach((el) => {
      const box = el.closest('compare-at-price') || el;
      if (totalCompare > totalFinal) {
        el.innerText = formatPrice(totalCompare);
        box.hidden = false;
      } else {
        box.hidden = true;
      }
    });
  }

  // ------------------------------------------------------------------------------------------------ the display cover
  // Acrylic or glass over the model (the CUBE): one at a time, a second click on the chosen one takes it off.
  function getSelectedDisplayCover() {
    return CF.STATE.choices.cover;
  }

  function cfInitAddonVariants() {
    const cover = CF.PRODUCT_DATA.cover;
    const acrylicIdEl = $('addon-acrylic-id');
    if (acrylicIdEl) {
      acrylicIdEl.value = cover.acrylicId;
      acrylicIdEl.setAttribute('data-price-cents', cover.acrylicPriceCents);
    }
    const glassIdEl = $('addon-glass-id');
    if (glassIdEl) {
      glassIdEl.value = cover.glassId;
      glassIdEl.setAttribute('data-price-cents', cover.glassPriceCents);
    }
    const priceAcrylic = $('price-acrylic-total');
    if (priceAcrylic) priceAcrylic.textContent = addonPrice(cover.acrylicPriceCents);
    const priceGlass = $('price-glass-total');
    if (priceGlass) priceGlass.textContent = addonPrice(cover.glassPriceCents);
  }

  // quiet: a cover put back as the page opens (THE SETUP IS KEPT) - no CustomizeProduct and no scrolling to the picture.
  function setDisplayCover(cover, quiet = false) {
    CF.STATE.choices.cover = cover;
    const acrylicId = $('addon-acrylic-id');
    const glassId = $('addon-glass-id');
    const acrylicWrapper = $('option-button-acrylic');
    const glassWrapper = $('option-button-glass');
    if (acrylicId) acrylicId.disabled = cover !== 'acrylic';
    if (glassId) glassId.disabled = cover !== 'glass';
    acrylicWrapper?.classList.toggle('active', cover === 'acrylic');
    glassWrapper?.classList.toggle('active', cover === 'glass');

    // The gallery shows the main picture, so the cover is seen landing on the model; not on taking it off.
    if (!quiet && (cover === 'acrylic' || cover === 'glass')) scrollCityIntoView('main');

    if (!quiet) pushDisplayCoverDL(cover);
    updateUI();
    keepSetup();
  }

  // The cover's window (assets/cf-cover-window.js) adds, changes or removes the cover through these: the cover chosen
  // ('none' without one), and choosing one - 'acrylic', 'glass' or 'none'.
  window.CFCover = { get: () => getSelectedDisplayCover(), set: (cover) => setDisplayCover(cover) };

  function updateDisplayCoverQuantities(count, displayCover) {
    [['acrylic', 'addon-acrylic-id', 'addon-acrylic-qty'], ['glass', 'addon-glass-id', 'addon-glass-qty']].forEach(([kind, idId, qtyId]) => {
      const idEl = $(idId);
      const qtyEl = $(qtyId);
      if (!idEl || !qtyEl) return;
      const on = displayCover === kind && count > 0;
      idEl.disabled = !on;
      qtyEl.disabled = !on;
      qtyEl.value = on ? count : 0;
    });
  }

  // ------------------------------------------------------------------------------------------------ the add-ons on the picture
  // The gallery's main picture wears what is chosen: the cover as a picture over it, the plaque as its text on the
  // frame (PLAQUE_GALLERY places it). magnifier-modal.js copies both into its close-up.
  function mainCard() {
    return $('configurator-carousel')?.querySelector('.main-variant-slide .cube_gallery_image_card') || null;
  }

  // The pictures that wear the add-ons: the main picture, or on a Set each city's card (snippets/cf-trio-cell), the
  // cover and the plaque of the city it shows.
  function galleryCards() {
    const cells = $('configurator-carousel')?.querySelectorAll('.cf-trio-cell .cube_gallery_image_card');
    if (cells && cells.length) return Array.from(cells);
    const card = mainCard();
    return card ? [card] : [];
  }

  // Brings the main picture into view (a Set has none: its first city's card): on a phone the gallery (above the
  // add-ons) and the slide inside it.
  function scrollCityIntoView(idx) {
    const track = document.querySelector('.cube-gallery-image');
    const card = idx === 'main'
      ? track?.querySelector('.cc_city-preview-wrapper.main-variant-slide') || track?.querySelector('.cc_city-preview-wrapper[data-city-index="1"]')
      : track?.querySelector(`.cc_city-preview-wrapper[data-city-index="${idx}"]`);
    if (!track || !card) return;
    if (isMobile()) {
      document.querySelector('.cube-gallery-wrapper')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      track.scrollTo({ left: card.offsetLeft - (track.clientWidth - card.clientWidth) / 2, behavior: 'smooth' });
      return;
    }
    card.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  // ROOM FOR THE COVER (2026-10-30): the card wears .has-cover while a cover is on, and the main picture makes room for
  // it - model, plaque and cover move down a little while the cover fades in (theme.css), and back as it fades out,
  // the cover taken out once it has. The class comes a frame after a new cover, so both start together.
  function refreshCoverOverlay(target, cover, forceRebuild = false) {
    const existing = target.querySelector('.cf-cover-overlay');
    if (cover === 'none') {
      target.classList.remove('has-cover');
      if (existing) {
        existing.dataset.leaving = 'true';
        setTimeout(() => { if (existing.dataset.leaving) existing.remove(); }, 450);
      }
      return;
    }
    const url = CF.PRODUCT_DATA?.overlay?.[cover];
    if (!url) return;
    // The attribute, not .src: the browser writes .src out in full (https:), the setting is protocol-relative, and the
    // two never matched - every update drew the cover again.
    if (existing && existing.getAttribute('src') === url && !forceRebuild) {
      delete existing.dataset.leaving;
      target.classList.add('has-cover');
      return;
    }
    if (existing) existing.remove();

    const ov = document.createElement('img');
    ov.className = 'cf-cover-overlay';
    ov.src = url;
    // The magnifier's close-up swaps to this; the card itself never loads it.
    const fullUrl = CF.PRODUCT_DATA?.overlayFull?.[cover];
    if (fullUrl) ov.dataset.imgFull = fullUrl;
    Object.assign(ov.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', zIndex: '10' });
    target.appendChild(ov);
    requestAnimationFrame(() => requestAnimationFrame(() => target.classList.add('has-cover')));
  }

  // cityIndex: whose plaque text the card shows (a Set's second card, the second city's).
  function refreshPlaqueOverlays(target, enabled, mobile, forceRebuild = false, cityIndex = 1) {
    let pl = target.querySelector('.plaque-overlay');
    if (!enabled) {
      if (pl) pl.remove();
      return;
    }
    const cfg = window.CFPlaque ? window.CFPlaque.getGalleryConfig('cube', mobile) : {};
    if (!pl || forceRebuild) {
      if (pl) pl.remove();
      pl = document.createElement('div');
      pl.className = 'plaque-overlay';
      pl.dataset.cityIndex = String(cityIndex);
      pl.innerHTML = `
      <span class="plaque-overlay-inner" data-city-index="${cityIndex}">
        <span class="plaque-text-head"></span>
        <span class="plaque-text-sub"></span>
      </span>`;
      target.appendChild(pl);
    }
    Object.assign(pl.style, {
      position: 'absolute', zIndex: '11', top: cfg.top, left: '50%', transform: 'translateX(-50%)',
      width: cfg.width || 'auto', height: cfg.height || 'auto', pointerEvents: 'none'
    });
    // Its height in % of the card, so it moves down with the picture (.has-cover, theme.css) by the card's measure.
    if (parseFloat(cfg.height)) pl.style.setProperty('--plaque-h', parseFloat(cfg.height));
    const mat = CF.STATE.plaqueMaterial || 'steel';
    pl.classList.toggle('plaque-overlay--steel', mat === 'steel');
    pl.classList.toggle('plaque-overlay--brass', mat === 'brass');
    window.CFPlaque?.syncToGallery();
  }

  function updateLiquidGallery(forceRebuild = false) {
    galleryCards().forEach((card, i) => {
      if (drawsCover()) refreshCoverOverlay(card, getSelectedDisplayCover(), forceRebuild);
      if (drawsPlaque()) refreshPlaqueOverlays(card, plaqueOn(), isMobile(), forceRebuild, i + 1);
    });
  }

  // The cover pictures only show once a cover is picked: fetched after the page has loaded.
  function cfPreloadOverlays() {
    const overlays = CF.PRODUCT_DATA?.overlay || {};
    [overlays.acrylic, overlays.glass].forEach((url) => {
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }

  // ------------------------------------------------------------------------------------------------ the plaque
  // reveal: false keeps the gallery where it is (the plaque designer turns the plaque on from its own window).
  // quiet: a plaque put back as the page opens (THE SETUP IS KEPT) - no CustomizeProduct.
  function setPlaque(type, reveal = true, quiet = false) {
    const isEnabled = type !== 'none';
    CF.STATE.choices.plaque = isEnabled;

    const fields = ['addon-plaque-id', 'addon-plaque-qty', 'addon-plaque-note'].map($);
    const designBtn = $('plaque-design-btn');
    const customizeCollapse = $('cc-plaque-customize-collapse');

    fields.forEach((el) => { if (el) el.disabled = !isEnabled; });
    if (customizeCollapse) customizeCollapse.classList.toggle('is-open', isEnabled);
    if (designBtn) designBtn.disabled = !isEnabled;

    if (isEnabled) {
      applyPlaqueVariant();
      updatePlaqueMaterialUI();
      // Where the plaque is drawn on the picture, the gallery shows it; not on taking it off.
      if (drawsPlaque() && reveal) scrollCityIntoView('main');
    } else {
      $('option-button-plaque-steel')?.classList.remove('active');
      $('option-button-plaque-brass')?.classList.remove('active');
    }

    if (!quiet) pushPlaqueDL(isEnabled ? CF.STATE.plaqueMaterial : 'none');
    updateUI();
    window.CFPlaque?.updateVisibility(1);
    keepSetup();
  }

  function updatePlaqueModalImage(material) {
    if (material !== 'steel' && material !== 'brass') return;
    document.querySelectorAll('.plaque-image-steel').forEach((img) => { img.style.display = material === 'steel' ? 'block' : 'none'; });
    document.querySelectorAll('.plaque-image-brass').forEach((img) => { img.style.display = material === 'brass' ? 'block' : 'none'; });
  }

  function updatePlaqueMaterialUI() {
    const steel = $('option-button-plaque-steel');
    const brass = $('option-button-plaque-brass');
    const mat = CF.STATE.plaqueMaterial || 'steel';
    const isEnabled = CF.STATE.choices.plaque;
    if (steel && brass) {
      steel.classList.toggle('active', isEnabled && mat === 'steel');
      brass.classList.toggle('active', isEnabled && mat === 'brass');
    }
    document.querySelectorAll('.plaque-overlay').forEach((pl) => {
      pl.classList.toggle('plaque-overlay--steel', mat === 'steel');
      pl.classList.toggle('plaque-overlay--brass', mat === 'brass');
    });
    updatePlaqueModalImage(mat);
  }

  function applyPlaqueVariant() {
    const addonPlaqueId = $('addon-plaque-id');
    if (!addonPlaqueId) return;
    const def = CF.CONSTANTS.PLAQUE_MATERIALS?.[CF.STATE.plaqueMaterial || 'steel'];
    if (!def) return;
    addonPlaqueId.value = def.id;
    addonPlaqueId.setAttribute('data-price-cents', def.priceCents);
  }

  // The plaque designer (snippets/modal_plaque_configurator): live preview, auto-fill from the city, emoji guard.
  window.CFPlaque = (() => {
    let initialized = false;
    const maxSlots = () => CF.CONSTANTS?.MAX_SLOTS || 1;

    function getVariantForSlot(i) {
      const sel = $(`City${i}`);
      if (!sel || !sel.value) return null;
      return cfGetVariantById(sel.value);
    }

    const stripFlagEmoji = (str) => str.replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '').trim();
    const isArabic = (text) => /[؀-ۿ]/.test(text);

    function getActiveCityCount() {
      let count = 0;
      for (let i = 1; i <= maxSlots(); i++) {
        const sel = $(`City${i}`);
        if (sel && sel.value && sel.value !== '') count++;
      }
      return Math.max(count, 1);
    }

    function previewSpan(text, css) {
      const span = document.createElement('span');
      span.textContent = text.toUpperCase().replace(/❤️|❤|♥/g, '♡');
      Object.assign(span.style, css);
      return span;
    }

    function updatePlaqueModalPreview(cityIndex) {
      const headlineInput = $(`plaque-note-${cityIndex}`);
      const subInput = $(`plaque-subnote-${cityIndex}`);
      const wrappers = document.querySelectorAll(`#plaque-preview-cityname-${cityIndex}, #modal-plaque-${cityIndex} .plaque-modal-container`);
      if (!headlineInput || wrappers.length === 0) return;

      wrappers.forEach((wrapper) => {
        let overlay = wrapper.querySelector('.plaque-modal-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'plaque-modal-overlay';
          wrapper.appendChild(overlay);
        }
        overlay.innerHTML = '';
        const widthPx = wrapper.clientWidth;

        const rawHeadline = headlineInput.value.trim();
        const h = previewSpan(rawHeadline, {
          fontSize: `${widthPx * 0.06}px`, fontWeight: 'bold', textTransform: 'uppercase', color: '#3d3d3d', lineHeight: '1'
        });
        if (isArabic(rawHeadline)) {
          h.style.direction = 'rtl';
          h.style.transform = 'translateY(-12%)';
        }
        overlay.appendChild(h);

        if (subInput && subInput.value.trim()) {
          overlay.appendChild(previewSpan(subInput.value.trim(), {
            fontSize: `${widthPx * 0.03}px`, lineHeight: '1', textTransform: 'uppercase', color: '#3d3d3d', marginTop: '2px'
          }));
        }
      });
    }

    // The text on the plaques drawn on the gallery (refreshPlaqueOverlays), squeezed to fit the plate: each plaque reads
    // the fields of its city (data-city-index; the main picture's is the first, a Set's cards one each).
    function syncPlaqueTextToGallery() {
      if (!plaqueOn()) return;
      document.querySelectorAll('.cc_city-preview-wrapper, .city-preview-wrapper').forEach((w) => w.querySelectorAll('.plaque-overlay').forEach((plaque) => {
        const inner = plaque.querySelector('.plaque-overlay-inner');
        if (!inner) return;

        const slot = plaque.dataset.cityIndex || '1';
        const headlineInput = $(`plaque-note-${slot}`);
        const subInput = $(`plaque-subnote-${slot}`);
        inner.innerHTML = '';
        const widthPx = plaque.clientWidth || 150;

        const h = previewSpan(headlineInput?.value.trim() || '', {
          fontSize: `${widthPx * 0.06}px`, fontWeight: 'bold', lineHeight: '1', textTransform: 'uppercase', color: '#3d3d3d'
        });
        h.className = 'plaque-text-head';
        inner.appendChild(h);
        if (subInput && subInput.value.trim()) {
          const sub = previewSpan(subInput.value.trim(), {
            fontSize: `${widthPx * 0.03}px`, lineHeight: '1', textTransform: 'uppercase', color: '#3d3d3d', marginTop: '1px'
          });
          sub.className = 'plaque-text-sub';
          inner.appendChild(sub);
        }

        requestAnimationFrame(() => {
          const maxW = plaque.clientWidth;
          const headEl = inner.querySelector('.plaque-text-head');
          if (!maxW || !headEl) return;
          const subEl = inner.querySelector('.plaque-text-sub');
          headEl.style.transform = '';
          if (subEl) subEl.style.transform = '';
          const widest = Math.max(headEl.offsetWidth, subEl ? subEl.offsetWidth : 0);
          if (widest > maxW && widest > 0) {
            inner.style.transform = `scale(${(maxW / widest) * 0.95})`;
            inner.style.transformOrigin = 'center';
          } else {
            inner.style.transform = '';
          }
        });
      }));
    }

    function syncPlaquePreviewVisibility(activeCount) {
      const plaqueEnabled = plaqueOn();
      for (let i = 1; i <= 6; i++) {
        const container = document.querySelector(`.preview-container-${i}`);
        if (container) container.style.display = plaqueEnabled && i <= activeCount ? '' : 'none';
      }
    }

    // Emoji are not engraved; hearts are (they engrave as ♡).
    function validatePlaqueInput(input) {
      const originalValue = input.value;
      let cleaned = originalValue
        .replace(/❤️/g, '___HEART_EMOJI___')
        .replace(/❤/g, '___HEART_CHAR___')
        .replace(/♥/g, '___HEART_PLAIN___')
        .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
        .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
        .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
        .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
        .replace(/[\u{1F900}-\u{1F9FF}]/gu, '')
        .replace(/[\u{1FA00}-\u{1FAFF}]/gu, '')
        .replace(/[\u{2600}-\u{26FF}]/gu, '')
        .replace(/[\u{2700}-\u{27BF}]/gu, '')
        .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
        .replace(/\|/g, '')
        .replace(/___HEART_EMOJI___/g, '❤️')
        .replace(/___HEART_CHAR___/g, '❤')
        .replace(/___HEART_PLAIN___/g, '♥');

      if (originalValue.replace(/❤️|❤|♥/g, '') !== cleaned.replace(/❤️|❤|♥/g, '')) {
        input.value = cleaned;
        showPlaqueError(input);
        return false;
      }
      return true;
    }

    function showPlaqueError(inputElement) {
      let popup = $('plaque-error-popup');
      if (!popup) {
        popup = document.createElement('div');
        popup.id = 'plaque-error-popup';
        Object.assign(popup.style, {
          position: 'fixed', backgroundColor: '#fff', color: '#333',
          padding: '12px 16px', borderRadius: '8px', boxShadow: '0px 4px 16px rgba(0,0,0,0.15)',
          border: '1px solid #e5e5e5', fontSize: '13px', zIndex: '999999', pointerEvents: 'none'
        });
        popup.textContent = CF.T.plaque_error.emoji_not_allowed;
        document.body.appendChild(popup);
      }
      const rect = inputElement.getBoundingClientRect();
      popup.style.left = `${rect.left}px`;
      popup.style.top = `${rect.bottom + 8}px`;
      popup.style.display = 'block';
      setTimeout(() => { popup.style.display = 'none'; }, 3000);
    }

    const HEADLINE = {
      cityname: (v) => v.baseCity || '',
      cityname_local: (v) => v.localname || v.baseCity || '',
      cityname_english: (v) => v.englishName || v.baseCity || '',
      countryname: (v) => stripFlagEmoji(v.countryname || ''),
      'cityname+countrycode': (v) => (v.baseCity ? `${v.baseCity}, ${v.countrycode || ''}`.trim() : '')
    };
    const SUBHEADLINE = {
      cityname: HEADLINE.cityname,
      cityname_local: HEADLINE.cityname_local,
      cityname_english: HEADLINE.cityname_english,
      countryname: HEADLINE.countryname,
      areaname: (v) => v.variantCity || '',
      coordinates: (v) => v.coordinates || '',
      scale: (v) => v.scale || '',
      none: () => ''
    };

    function applyAutoFill() {
      const autoHeadEl = $('auto-headline');
      const autoSubEl = $('auto-subheadline');
      if (!autoHeadEl || !autoSubEl) return;

      for (let i = 1; i <= getActiveCityCount(); i++) {
        const v = getVariantForSlot(i);
        const hIn = $(`plaque-note-${i}`);
        const sIn = $(`plaque-subnote-${i}`);
        if (!hIn || !sIn || !v) continue;
        if (HEADLINE[autoHeadEl.value]) hIn.value = HEADLINE[autoHeadEl.value](v);
        if (SUBHEADLINE[autoSubEl.value]) sIn.value = SUBHEADLINE[autoSubEl.value](v);
        updatePlaqueModalPreview(i);
      }
      syncPlaqueTextToGallery();
    }

    // Hide "City Name (Local)" / "(English)" where they read the same as "City Name" (München on /de, any city on
    // /en), and "Area name" where there is no area ("Paris", not "New York - Midtown") - on a Set or Collection only
    // where that holds for every city: Athens having no local name must not take the option away from Cairo.
    function refreshAutofillOptions() {
      const variants = [];
      for (let i = 1; i <= getActiveCityCount(); i++) {
        const v = getVariantForSlot(i);
        if (v) variants.push(v);
      }
      if (!variants.length) return;
      const distinct = (v, name) => !!v[name] && v[name].trim() !== (v.baseCity || '').trim();
      const localRedundant = !variants.some((v) => distinct(v, 'localname'));
      const englishRedundant = !variants.some((v) => distinct(v, 'englishName'));
      ['auto-headline', 'auto-subheadline'].forEach((id) => {
        const sel = $(id);
        if (!sel) return;
        const locOpt = sel.querySelector('option[value="cityname_local"]');
        const engOpt = sel.querySelector('option[value="cityname_english"]');
        if (locOpt) { locOpt.hidden = localRedundant; if (localRedundant && sel.value === 'cityname_local') sel.value = ''; }
        if (engOpt) { engOpt.hidden = englishRedundant; if (englishRedundant && sel.value === 'cityname_english') sel.value = ''; }
      });
      const areaEmpty = !variants.some((v) => v.variantCity && v.variantCity.trim());
      const subSel = $('auto-subheadline');
      const areaOpt = subSel && subSel.querySelector('option[value="areaname"]');
      if (areaOpt) { areaOpt.hidden = areaEmpty; if (areaEmpty && subSel.value === 'areaname') subSel.value = ''; }
    }

    function bindEventHandlers() {
      for (let i = 1; i <= 6; i++) {
        const hIn = $(`plaque-note-${i}`);
        const sIn = $(`plaque-subnote-${i}`);
        if (hIn) hIn.addEventListener('input', () => { validatePlaqueInput(hIn); updatePlaqueModalPreview(i); syncPlaqueTextToGallery(); });
        if (sIn) sIn.addEventListener('input', () => { validatePlaqueInput(sIn); updatePlaqueModalPreview(i); syncPlaqueTextToGallery(); });
      }
      $('auto-headline')?.addEventListener('change', applyAutoFill);
      $('auto-subheadline')?.addEventListener('change', applyAutoFill);
    }

    return {
      init() {
        if (initialized) return;
        bindEventHandlers();
        $('plaque-design-btn')?.addEventListener('click', () => {
          setTimeout(() => {
            syncPlaquePreviewVisibility(getActiveCityCount());
            refreshAutofillOptions();
            updatePlaqueModalPreview(1);
          }, 50);
        });
        initialized = true;
      },
      updatePreview: (i) => updatePlaqueModalPreview(i),
      syncToGallery: () => syncPlaqueTextToGallery(),
      updateVisibility: (count) => syncPlaquePreviewVisibility(count),
      applyAutoFill: () => applyAutoFill(),
      getGalleryConfig: (size, mobile) => (CF.CONSTANTS?.PLAQUE_GALLERY || {})[mobile ? 'mobile' : 'desktop'] || {},
      // The plaque designer's window (assets/cf-plaque-designer.js) adds, changes or removes the plaque through these:
      // the material it is in ('none' without one), and setting it - 'steel', 'brass' or 'none'.
      material: () => (plaqueOn() ? CF.STATE.plaqueMaterial || 'steel' : 'none'),
      setMaterial: (mat) => {
        if (mat === 'steel' || mat === 'brass') {
          CF.STATE.plaqueMaterial = mat;
          setPlaque(mat, false);
        } else {
          setPlaque('none', false);
        }
      }
    };
  })();

  // ------------------------------------------------------------------------------------------------ add to cart
  const allATC = () => document.querySelectorAll('.add-all-to-cart');

  // A plaque without text is said in a browser dialog, as on the CUBE page: the text field is in the plaque designer,
  // out of view by the time Add to cart is clicked, so a line above the button reads as nothing happening. On the FRAME
  // it said nothing at all until 2026-10-20 - the page has no .error-note line.
  function validateSelections() {
    const sel = soldInput();
    if (!sel || !sel.value) return { valid: false, message: CF.T.validation.select_city };
    if (CF.CART.stock) {
      const v = cfGetVariantById(sel.value);
      if (v && cfIsOutOfStock(v)) return { valid: false, message: CF.T.validation.city_out_of_stock };
    }
    // A plaque is made for every city it is bought for - one per city on a Set or Collection, all of them in the total -
    // so each needs its headline, or an empty plaque would be engraved (Alex, 2026-10-23). It was the first city's only,
    // and the others' plaques were counted in the total but left out of the cart.
    if (CF.STATE.choices.plaque === true) {
      for (let i = 1; i <= addonQty(); i++) {
        const headline = $(`plaque-note-${i}`);
        if (!headline || !headline.value.trim()) return { valid: false, message: CF.T.validation.enter_plaque_text, kind: 'alert' };
      }
    }
    return { valid: true, message: null };
  }

  function updateAddToCartState() {
    const blocked = !!CF.CART.stock && cfIsOutOfStock(cfGetSelectedVariant(1));
    allATC().forEach((btn) => {
      btn.disabled = blocked;
      btn.classList.toggle('disabled', blocked);
      btn.classList.toggle('enabled', !blocked);
      btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      btn.style.opacity = blocked ? '0.5' : '1';
      btn.style.cursor = blocked ? 'not-allowed' : 'pointer';
    });
    if (!blocked) clearError();
  }

  function lockATCButtons(disabled, label) {
    allATC().forEach((btn) => {
      btn.disabled = disabled;
      btn.innerText = label || CF.T.atc.add_to_cart;
      btn.classList.toggle('disabled', disabled);
      btn.classList.toggle('enabled', !disabled);
      btn.style.opacity = disabled ? '0.5' : '1';
      btn.style.cursor = disabled ? 'not-allowed' : 'pointer';
    });
  }

  function buildCartItems() {
    const sel = soldInput();
    if (!sel || !sel.value) return null;

    const items = [{ id: parseInt(sel.value, 10), quantity: 1 }];
    // Where the page asks for it (the FRAME): the ship window the shipping line shows (window.cfShipEstimate,
    // snippets/cf-delivery-data), else the section's production window; none for a variant from stock. The property's
    // name is localized and the same on GRAND and BLOK; the English literal only covers a missing label.
    if (CF.CART.shipping) {
      const shippingLabel = window.CF_SHIPPING_LABEL || 'Estimated Shipping';
      const shippingWindow = window.cfShipEstimate && window.cfShipEstimate();
      items[0].properties = shippingWindow ? { [shippingLabel]: shippingWindow } : {};
    }

    // A Set or Collection is one variant whose cities are fixed on it in the catalogue; its add-ons come once per city:
    // one cover line of that quantity, and a plaque line for each city with text, naming the city it belongs to.
    if (sellsCover()) {
      const cover = getSelectedDisplayCover();
      if (cover === 'acrylic') items.push({ id: parseInt(CF.PRODUCT_DATA.cover.acrylicId, 10), quantity: addonQty() });
      else if (cover === 'glass') items.push({ id: parseInt(CF.PRODUCT_DATA.cover.glassId, 10), quantity: addonQty() });
    }

    const plaqueIdEl = $('addon-plaque-id');
    if (plaqueIdEl && !plaqueIdEl.disabled) {
      for (let i = 1; i <= addonQty(); i++) {
        const head = $(`plaque-note-${i}`)?.value.trim() || '';
        const sub = $(`plaque-subnote-${i}`)?.value.trim() || '';
        if (!head) continue;
        // No Sub-Headline line on the order when it is empty (Alex, 2026-10-16).
        const props = { Headline: head };
        if (CF.CART.perCity) props.City = CF.CART.cities?.[i - 1] || '';
        if (sub) props['Sub-Headline'] = sub;
        items.push({ id: parseInt(plaqueIdEl.value, 10), quantity: 1, properties: props });
      }
    }
    return items;
  }

  async function postCartItems(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, attributes: { [CF.CART.attribute]: 'true' } })
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.description || res.statusText);
    }
    return res.json();
  }

  function openCartDrawerOrRedirect() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.show === 'function') cartDrawer.show();
    else window.location.href = '/cart';
  }

  async function handleAddToCart(e) {
    e.preventDefault();
    const { valid, message, kind } = validateSelections();
    if (!valid) {
      // kind 'alert': a message that would go unseen in the line above the button, as a browser dialog instead.
      if (kind === 'alert') alert(message);
      else showError(message);
      return;
    }
    if (!allATC().length) return;

    lockATCButtons(true, CF.T.atc.adding);
    try {
      const items = buildCartItems();
      if (!items) return;
      await postCartItems(items);
      forgetSetup();
      // cart:refresh has the drawer, count and dot read the cart from the server. Not cart:change: the theme's listeners
      // want a full cart with the drawer's sections in it, which the add response is not (it threw in the drawer).
      document.dispatchEvent(new Event('cart:refresh'));
      openCartDrawerOrRedirect();
    } catch (err) {
      console.error('Add to cart failed', err);
      // An add past a cover's or plaque's stock is refused with its own message (assets/cf-addon-stock.js).
      alert((window.CFAddonStock && window.CFAddonStock.takeMessage()) || CF.T.atc.error_generic);
    } finally {
      lockATCButtons(false);
    }
  }

  // ------------------------------------------------------------------------------------------------ gallery
  function slideIndexAt(area) {
    const slides = Array.from(area.querySelectorAll('.cc_city-preview-wrapper'));
    return slides.reduce((closest, slide, i) => {
      const distance = Math.abs(slide.offsetLeft - area.offsetLeft - area.scrollLeft);
      return distance < closest.distance ? { index: i, distance } : closest;
    }, { index: 0, distance: Infinity }).index;
  }

  function updateDots(area) {
    const dots = document.querySelectorAll('#configurator-dots button');
    if (!dots.length) return;
    const index = slideIndexAt(area);
    dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }

  function initGalleryNav() {
    const wrapper = document.querySelector('.cube-gallery-wrapper');
    const scrollArea = $('configurator-carousel');
    const prevBtn = wrapper?.querySelector('.cf-gallery-nav-button--prev');
    const nextBtn = wrapper?.querySelector('.cf-gallery-nav-button--next');
    if (!scrollArea) return;

    const getSlides = () => Array.from(scrollArea.querySelectorAll('.cc_city-preview-wrapper'));
    const getSlideLeft = (slide) => (slide ? slide.offsetLeft - scrollArea.offsetLeft : 0);
    const scrollToSlide = (index) => {
      const slides = getSlides();
      const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (target) scrollArea.scrollTo({ left: getSlideLeft(target), behavior: 'smooth' });
    };

    if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); scrollToSlide(slideIndexAt(scrollArea) - 1); });
    if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); scrollToSlide(slideIndexAt(scrollArea) + 1); });

    const updateUIState = () => {
      const maxScroll = scrollArea.scrollWidth - scrollArea.clientWidth;
      prevBtn?.classList.toggle('is-at-limit', scrollArea.scrollLeft <= 5);
      nextBtn?.classList.toggle('is-at-limit', scrollArea.scrollLeft >= maxScroll - 5);
      updateDots(scrollArea);
    };
    scrollArea.addEventListener('scroll', updateUIState, { passive: true });
    setTimeout(updateUIState, 150);
  }

  function triggerGalleryWaterfall() {
    document.querySelectorAll('img[data-deferred-low]').forEach((img) => {
      if (img.dataset.deferredLow) {
        img.src = img.dataset.deferredLow;
        delete img.dataset.deferredLow;
      }
    });
  }

  // The slide shows the upgrade at its own size. The 2048 is the magnifier's (it reads data-img-full itself); a phone
  // slide (~390px at 3x) needs ~1200px, and nothing at all where the 700 preview already covers the screen.
  function upgradeUrlForScreen(img, highUrl) {
    const need = Math.ceil(img.clientWidth * (window.devicePixelRatio || 1));
    if (!need || !/[?&]width=2048(?!\d)/.test(highUrl || '')) return highUrl;
    const shown = img.naturalWidth || Number(((img.currentSrc || img.src || '').match(/[?&]width=(\d+)/) || [])[1]) || 0;
    if (shown && need <= shown) return null;
    const width = [1000, 1200, 1600].find((w) => w >= need);
    return width ? highUrl.replace(/([?&]width=)2048(?!\d)/, `$1${width}`) : highUrl;
  }

  // The sharper copy takes the picture's place once it is fetched and decoded - the same picture, so nothing to fade.
  // It used to fade in over the picture for .4s, and with the pictures multiplied into the grey box (theme.css,
  // PRODUCT GALLERIES ON THE TILES' GREY) the two multiplied into each other: the picture went dark for about a
  // second on every load (Alex, 2026-10-30, Safari and Chrome).
  function performHighResUpgrade(baseImg, highUrl, isMainVariant) {
    highUrl = upgradeUrlForScreen(baseImg, highUrl);
    if (!highUrl || highUrl === baseImg.src) {
      if (isMainVariant) triggerGalleryWaterfall();
      return;
    }
    const hiResImg = new Image();
    hiResImg.src = highUrl;
    hiResImg.decode().then(() => {
      baseImg.src = highUrl;
      if (isMainVariant) triggerGalleryWaterfall();
    }, () => {});
  }

  function initLiquidGallery() {
    const container = $('configurator-carousel');
    const dotContainer = $('configurator-dots');
    if (!container) return;

    // A Set's cities side by side (snippets/cf-trio-cell), all wanted at once: each card's sharper copy straight away,
    // no main slide, dots or arrows (its scroll bar is snippets/cf-set-scroll's).
    const cells = container.querySelectorAll('.cf-trio-cell');
    if (cells.length) {
      cells.forEach((cell) => {
        const img = cell.querySelector('.preview_image_img');
        const card = cell.querySelector('.cube_gallery_image_card');
        if (!img || !card || !cell.dataset.highUrl) return;
        const upgrade = () => performHighResUpgrade(img, cell.dataset.highUrl, false);
        if (img.complete && img.currentSrc) upgrade();
        else img.addEventListener('load', upgrade, { once: true });
      });
      updateLiquidGallery(true);
      return;
    }

    const slides = Array.from(container.querySelectorAll('.cc_city-preview-wrapper'));
    const dots = Array.from(dotContainer?.querySelectorAll('button') || []);
    const isSingleImage = slides.length <= 1;

    [document.querySelector('.cf-gallery-nav-button--prev'), document.querySelector('.cf-gallery-nav-button--next'), dotContainer]
      .forEach((el) => { if (el) el.classList.toggle('single-image', isSingleImage); });

    dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => {
        const slide = slides[idx];
        if (slide) container.scrollTo({ left: slide.offsetLeft - container.offsetLeft, behavior: 'smooth' });
      });
    });

    const mainSlide = container.querySelector('.main-variant-slide');
    const mainImg = mainSlide?.querySelector('.preview_image_img');
    const mainCard = mainSlide?.querySelector('.cube_gallery_image_card');
    const finishMain = () => {
      if (!mainSlide || !mainImg || !mainCard || mainSlide.dataset.mainHydrated === 'true') return;
      mainSlide.dataset.mainHydrated = 'true';
      performHighResUpgrade(mainImg, mainSlide.dataset.highUrl, true);
    };

    // The main slide's sharper copy and the other slides' first pictures wait for the page's warm-up
    // (cf-deferred-media.js), or for the visitor to swipe the gallery: at DOMContentLoaded they competed with the
    // first screen.
    container.addEventListener('scroll', triggerGalleryWaterfall, { once: true, passive: true });
    if (mainImg) {
      (window.cfWhenWarm || ((fn) => fn()))(() => {
        if (mainImg.complete && mainImg.currentSrc) finishMain();
        else mainImg.addEventListener('load', finishMain, { once: true });
      });
    } else {
      triggerGalleryWaterfall();
    }

    // Each further slide gets its sharper copy once it is nearly in view.
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const slide = entry.target;
        const img = slide.querySelector('.preview_image_img');
        const highUrl = slide.dataset.highUrl;
        if (!img || !highUrl || slide.classList.contains('is-upgraded')) return;
        const upgrade = () => {
          performHighResUpgrade(img, highUrl, false);
          slide.classList.add('is-upgraded');
          observer.unobserve(slide);
        };
        if (img.src && !img.src.includes('data:image')) {
          upgrade();
        } else {
          const check = setInterval(() => {
            if (img.src && !img.src.includes('data:image')) { upgrade(); clearInterval(check); }
          }, 100);
        }
      });
    }, { root: container, rootMargin: '0px 100% 0px 0px', threshold: 0.01 });
    // Not the rotation slide: it swaps its own picture per angle (assets/cf-rotation.js), and a sharper copy from here
    // would overwrite whichever angle is showing.
    slides.forEach((slide) => {
      if (slide.classList.contains('main-variant-slide') || slide.classList.contains('cc-rotation-slide')) return;
      observer.observe(slide);
    });

    if (drawsCover() || drawsPlaque()) updateLiquidGallery(true);
    initGalleryNav();
  }

  // ------------------------------------------------------------------------------------------------ modals
  // While a modal or the magnifier is open the page under it stays where it was.
  function cfLockScroll() {
    if (CF.STATE.scroll.locked) return;
    CF.STATE.scroll.savedY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('cf-modal-locked');
    document.body.classList.add('cf-modal-locked');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${CF.STATE.scroll.savedY}px`;
    document.body.style.width = '100%';
    CF.STATE.scroll.locked = true;
  }

  function cfUnlockScroll() {
    if (!CF.STATE.scroll.locked) return;
    document.documentElement.classList.remove('cf-modal-locked');
    document.body.classList.remove('cf-modal-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    CF.STATE.scroll.locked = false;
    window.scrollTo({ top: CF.STATE.scroll.savedY, behavior: 'auto' });
  }

  function cfSyncScrollLock() {
    const anyModalOpen = !!document.querySelector('x-modal[open]');
    if (anyModalOpen && !CF.STATE.scroll.locked) cfLockScroll();
    else if (!anyModalOpen && CF.STATE.scroll.locked) cfUnlockScroll();
  }

  function cfInitScrollLock() {
    const modalObserver = new MutationObserver(() => requestAnimationFrame(cfSyncScrollLock));
    document.querySelectorAll('x-modal').forEach((modal) => {
      modalObserver.observe(modal, { attributes: true, attributeFilter: ['open', 'class', 'style'] });
    });
    cfSyncScrollLock();
    CF.STATE.scroll.observer = modalObserver;
  }

  // ------------------------------------------------------------------------------------------------ start
  function updateUI() {
    updateTotalSummary();
    if (drawsCover() || drawsPlaque()) updateLiquidGallery();
    if (sellsCover()) {
      updateDisplayCoverQuantities(1, getSelectedDisplayCover());
      updateDisplayCoverTotal();
    }
    updatePlaqueTotal();
    if (window.CFPlaque) {
      window.CFPlaque.updateVisibility(1);
      window.CFPlaque.updatePreview(1);
      window.CFPlaque.syncToGallery();
    }
    updateAddToCartState();
    clearError();
  }

  // Clicking the selected material turns the plaque off - there is no "none" button. The second listener on each is
  // guarded on addon-plaque-id being disabled, so it sits out the deselect the first one has just made.
  function pickMaterial(mat) {
    if (CF.STATE.choices.plaque && CF.STATE.plaqueMaterial === mat) {
      setPlaque('none');
      return;
    }
    CF.STATE.plaqueMaterial = mat;
    setPlaque(mat);
  }

  function refreshMaterial(mat) {
    if (!plaqueOn()) return;
    CF.STATE.plaqueMaterial = mat;
    updatePlaqueMaterialUI();
    applyPlaqueVariant();
    updatePlaqueTotal();
    updateTotalSummary();
  }

  function initAddonBindings() {
    // A second click on the chosen cover takes it off.
    cfSafeOn('display-cover-acrylic', 'click', () => setDisplayCover(getSelectedDisplayCover() === 'acrylic' ? 'none' : 'acrylic'));
    cfSafeOn('display-cover-glass', 'click', () => setDisplayCover(getSelectedDisplayCover() === 'glass' ? 'none' : 'glass'));
    cfSafeOn('option-button-plaque-steel', 'click', () => pickMaterial('steel'));
    cfSafeOn('option-button-plaque-brass', 'click', () => pickMaterial('brass'));
    cfSafeOn('option-button-plaque-steel', 'click', () => refreshMaterial('steel'));
    cfSafeOn('option-button-plaque-brass', 'click', () => refreshMaterial('brass'));
    allATC().forEach((btn) => btn.addEventListener('click', handleAddToCart));
  }

  // ------------------------------------------------------------------------------------------------ the setup is kept
  /* THE SETUP IS KEPT (2026-11-01, Alex: "remember the last setup", as the Collection and the Set do): the display
     cover, the plaque and the plaque's words, in this browser's localStorage under the key the page names (CF.CART.keep;
     the CUBE page's cf-cube-setup). PER CITY (Alex, 2026-11-01: across cities is "creepy"): each city's own setup, put
     back on that city only, the words exactly as they were (assets/cf-plaque-designer.js, CFPlaqueWords); another city
     opens plain. Seven days, then dropped, and the 20 cities last set up at most; a city's is dropped once it is in the
     cart; nothing kept where storage is refused (a private window, a full store). Nothing leaves the browser.

     Written when a cover or a plaque is added, changed or removed (setDisplayCover, setPlaque), never while the kept
     one is being put back (keepReady); a city with neither is not kept. Put back as the page opens: an add-on out of
     stock now is not, one that is comes back quietly - no CustomizeProduct, which is for a shopper's pick, and no
     scrolling to the picture. Version 1 (the first hour of 2026-11-01, one setup for every city) is dropped unread. */
  const KEEP_MS = 7 * 24 * 60 * 60 * 1000;
  const KEEP_CITIES = 20;
  let keepReady = false;

  const keptCity = () => String(soldInput()?.value || '');

  // { [variant id]: { at, cover, plaque, words } }, the expired left out.
  function readKept() {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(CF.CART.keep)); } catch (_) { return {}; }
    if (saved && saved.v !== 2) writeKept({});
    if (!saved || saved.v !== 2 || !saved.cities) return {};
    const now = Date.now();
    return Object.fromEntries(Object.entries(saved.cities).filter(([, s]) => s && now - Number(s.at) < KEEP_MS));
  }

  function writeKept(cities) {
    const newest = Object.entries(cities).sort((a, b) => b[1].at - a[1].at).slice(0, KEEP_CITIES);
    try {
      if (newest.length) localStorage.setItem(CF.CART.keep, JSON.stringify({ v: 2, cities: Object.fromEntries(newest) }));
      else localStorage.removeItem(CF.CART.keep);
    } catch (_) { }
  }

  function keepSetup() {
    if (!keepReady || !CF.CART?.keep || !keptCity()) return;
    const cities = readKept();
    const cover = getSelectedDisplayCover();
    const plaque = window.CFPlaque?.material?.() || 'none';
    if (cover === 'none' && plaque === 'none') delete cities[keptCity()];
    else cities[keptCity()] = { at: Date.now(), cover, plaque, words: plaque === 'none' ? null : window.CFPlaqueWords?.get() || null };
    writeKept(cities);
  }

  function forgetSetup() {
    if (!CF.CART?.keep || !keptCity()) return;
    const cities = readKept();
    delete cities[keptCity()];
    writeKept(cities);
  }

  // An add-on's material that can be chosen here: its tile in the window, not greyed out of stock.
  const choosable = (selector) => {
    const tile = document.querySelector(selector);
    return !!tile && !tile.classList.contains('add-on-disabled');
  };

  function restoreSetup() {
    const saved = CF.CART?.keep && keptCity() && readKept()[keptCity()];
    if (saved) {
      if (sellsCover() && ['acrylic', 'glass'].includes(saved.cover) && choosable(`[data-cover-material="${saved.cover}"]`)) {
        setDisplayCover(saved.cover, true);
      }
      if (['steel', 'brass'].includes(saved.plaque) && Array.isArray(saved.words) && window.CFPlaqueWords
        && choosable(`[data-plaque-material="${saved.plaque}"]`)) {
        window.CFPlaqueWords.put(saved.words);
        if ($('plaque-note-1')?.value.trim()) {
          CF.STATE.plaqueMaterial = saved.plaque;
          setPlaque(saved.plaque, false, true);
        }
      }
      // The buy box's rows for the cover and the plaque follow (assets/cf-cover-window.js, cf-plaque-designer.js).
      document.dispatchEvent(new CustomEvent('cf:addon-change'));
    }
    keepReady = true;
  }

  // The gallery runs on every product page; the buy box's parts only where one is (its settings: CF.CART).
  function init() {
    if (CF.CART) {
      if (sellsCover()) cfInitAddonVariants();
      initAddonBindings();
      window.CFPlaque.init();
      window.CFPlaque.applyAutoFill();
      updateUI();
      restoreSetup();
    }
    initLiquidGallery();
    cfInitScrollLock();
    watchGallerySwap();
    if (drawsCover()) (window.cfWhenWarm || ((fn) => fn()))(cfPreloadOverlays);
  }

  // A variant picker re-renders the section and the theme swaps the gallery for the new variant's (theme.js, partial
  // re-render, data-block-type="pdp-gallery"): the new one starts like the first.
  function watchGallerySwap() {
    document.querySelectorAll('product-rerender').forEach((host) => {
      new MutationObserver((records) => {
        const swapped = records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches('.pdp-gallery') || n.querySelector?.('.pdp-gallery'))));
        if (swapped) initLiquidGallery();
      }).observe(host, { childList: true, subtree: true });
    });
  }

  // At DOMContentLoaded, as the inline script did: this file is deferred and runs just before it, while deferred
  // scripts further down the page (the warm-up's cfWhenWarm, theme.js's elements) have not run yet.
  document.addEventListener('DOMContentLoaded', init);
})();
